import { useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react'
import {
  ArrowUpIcon,
  ArrowDownIcon,
  XMarkIcon,
  PlusIcon,
  PhotoIcon,
  ArrowUpTrayIcon,
  BoldIcon,
  LinkIcon,
} from '@heroicons/react/24/outline'
import type { NewsBlock, RouteInfo } from '../../api/news'
import { newKey } from './newsTheme'
import {
  toggleBold, applyLink, removeLink, linkContext, isHttpsUrl, inlineProblem,
  stripListMarker, hasNumberPrefix, hasBulletPrefix, looksNumbered, MOD_KEY, type Edit,
} from './inlineFormat'

// Editor for the content blocks. Each block keeps a local `key` for React;
// it is stripped before saving (the server rejects unknown fields).

export type EditorBlock = NewsBlock & { key: string }


const KIND_LABEL: Record<NewsBlock['type'], string> = {
  heading: 'Heading', paragraph: 'Paragraph', list: 'Bullet list', callout: 'Callout', image: 'Image', button: 'Button',
}

function blank(type: NewsBlock['type']): EditorBlock {
  const key = newKey()
  switch (type) {
    case 'heading': return { key, type, text: '' }
    case 'paragraph': return { key, type, text: '' }
    case 'list': return { key, type, items: [''] }
    case 'callout': return { key, type, tone: 'info', text: '' }
    case 'image': return { key, type, url: '', alt: '' }
    case 'button': return { key, type, label: 'Open', target: 'app:news' }
  }
}

const input = 'w-full text-sm rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500'

const toolBtn = 'inline-flex items-center justify-center w-6 h-6 rounded text-slate-500 hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'

type LinkDraft = { start: number; end: number; label: string; url: string; editing: boolean }

/** "example.com/x" -> "https://example.com/x"; anything with a scheme is left alone. */
function withScheme(url: string): string {
  const u = url.trim()
  if (!u || /^[a-z][a-z0-9+.-]*:/i.test(u)) return u
  return `https://${u.replace(/^\/+/, '')}`
}

function LinkPopover({ draft, onApply, onRemove, onCancel }: {
  draft: LinkDraft
  onApply: (label: string, url: string) => void
  onRemove: () => void
  onCancel: () => void
}) {
  const [label, setLabel] = useState(draft.label)
  const [url, setUrl] = useState(draft.url)
  const [tried, setTried] = useState(false)
  const full = withScheme(url)
  const error = !full ? 'Enter a web address.'
    : /^http:\/\//i.test(full) ? 'Links must use https://, not http://.'
    : !isHttpsUrl(full) ? 'Use a full address like https://example.com/page.' : ''
  const apply = () => { setTried(true); if (!error) onApply(label, full) }
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); apply() }
    else if (e.key === 'Escape') { e.preventDefault(); onCancel() }
  }
  return (
    <div role="dialog" aria-label={draft.editing ? 'Edit link' : 'Add link'} className="absolute left-0 top-7 z-20 w-full max-w-sm rounded-lg border border-slate-200 bg-white p-2.5 shadow-lg space-y-1.5" onKeyDown={onKey}>
      <input className={input} value={label} maxLength={200} onChange={(e) => setLabel(e.target.value)} placeholder="Link text" aria-label="Link text" />
      <input className={input} value={url} maxLength={500} autoFocus onChange={(e) => setUrl(e.target.value.trim())} placeholder="https://" aria-label="Link address" />
      {tried && error && <p className="text-[11px] text-red-600">{error}</p>}
      <div className="flex items-center gap-1.5 pt-0.5">
        {draft.editing && <button type="button" onClick={onRemove} className="text-[11px] font-semibold text-slate-500 hover:text-red-600">Remove link</button>}
        <button type="button" onClick={onCancel} className="ml-auto px-2 py-1 text-[11px] font-semibold rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-slate-700">Cancel</button>
        <button type="button" onClick={apply} className="px-2 py-1 text-[11px] font-semibold rounded-md bg-blue-600 text-white hover:bg-blue-700">{draft.editing ? 'Update' : 'Add link'}</button>
      </div>
    </div>
  )
}

/**
 * A text field with Bold and Link. Stores the server's markdown-lite
 * (**bold**, [text](https://...)); the buttons and Cmd/Ctrl+B / Cmd/Ctrl+K
 * write that syntax so authors never have to type it.
 */
function FormatField({ value, onChange, label, placeholder, multiline = false, rows = 3, maxLength, bold = true, onLeave }: {
  value: string
  onChange: (v: string) => void
  label: string
  placeholder?: string
  multiline?: boolean
  rows?: number
  maxLength?: number
  /** false on headings, which are bold already */
  bold?: boolean
  /** focus left the field and its toolbar/popover */
  onLeave?: () => void
}) {
  const wrap = useRef<HTMLDivElement>(null)
  const el = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
  const pending = useRef<{ start: number; end: number } | null>(null)
  const [link, setLink] = useState<LinkDraft | null>(null)
  const [note, setNote] = useState('')

  // Restore the selection after a formatting edit re-renders the field.
  useLayoutEffect(() => {
    const p = pending.current
    const node = el.current
    if (!p || !node) return
    pending.current = null
    node.focus()
    node.setSelectionRange(p.start, p.end)
  })

  const selection = (): [number, number] => {
    const n = el.current
    return n ? [n.selectionStart ?? value.length, n.selectionEnd ?? value.length] : [value.length, value.length]
  }
  const commit = (e: Edit) => {
    if (maxLength && e.text.length > maxLength) { setNote(`That would go over the ${maxLength}-character limit.`); return }
    setNote('')
    pending.current = { start: e.start, end: e.end }
    onChange(e.text)
  }
  const doBold = () => {
    if (!bold) { setNote('Headings are already bold.'); return }
    const [s, e] = selection()
    commit(toggleBold(value, s, e))
  }
  const openLink = () => {
    const [s, e] = selection()
    if (value.slice(s, e).includes('\n')) { setNote('Select text on one line to link it.'); return }
    setNote('')
    setLink({ start: s, end: e, ...linkContext(value, s, e) })
  }
  const closeLink = () => {
    if (link) pending.current = { start: link.start, end: link.end }
    setLink(null)
  }
  const onKeyDown = (ev: KeyboardEvent) => {
    if (!(ev.metaKey || ev.ctrlKey) || ev.altKey || ev.shiftKey) return
    const k = ev.key.toLowerCase()
    if (k === 'b') { ev.preventDefault(); doBold() }
    else if (k === 'k') { ev.preventDefault(); openLink() }
  }

  const problem = inlineProblem(value)
  const common = {
    ref: (n: HTMLInputElement | HTMLTextAreaElement | null) => { el.current = n },
    className: multiline ? `${input} resize-y` : input,
    value, maxLength, placeholder, 'aria-label': label,
    onKeyDown,
  }
  return (
    <div
      ref={wrap}
      className="relative"
      onBlur={(e) => {
        if (wrap.current?.contains(e.relatedTarget as Node | null)) return
        setLink(null)
        onLeave?.()
      }}
    >
      <div className="flex items-center gap-0.5 mb-1" role="toolbar" aria-label={`${label} formatting`}>
        <button
          type="button"
          className={`${toolBtn} ${bold ? '' : 'opacity-40 cursor-not-allowed hover:bg-transparent hover:text-slate-500'}`}
          aria-label={bold ? `Bold (${MOD_KEY}+B)` : 'Bold (not available: headings are already bold)'}
          aria-disabled={!bold}
          title={bold ? `Bold (${MOD_KEY}+B)` : 'Headings are already bold'}
          onMouseDown={(e) => e.preventDefault()}
          onClick={doBold}
        ><BoldIcon className="w-3.5 h-3.5" /></button>
        <button
          type="button"
          className={toolBtn}
          aria-label={`Link (${MOD_KEY}+K)`}
          title={`Link (${MOD_KEY}+K)`}
          aria-haspopup="dialog"
          aria-expanded={!!link}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => (link ? closeLink() : openLink())}
        ><LinkIcon className="w-3.5 h-3.5" /></button>
        {note && <span className="ml-1.5 text-[11px] text-slate-500" role="status">{note}</span>}
      </div>
      {multiline
        ? <textarea {...common} rows={rows} onChange={(e) => onChange(e.target.value)} />
        : <input {...common} onChange={(e) => onChange(e.target.value)} />}
      {link && (
        <LinkPopover
          draft={link}
          onCancel={closeLink}
          onRemove={() => { setLink(null); commit(removeLink(value, link.start, link.end)) }}
          onApply={(text, url) => { setLink(null); commit(applyLink(value, link.start, link.end, text, url)) }}
        />
      )}
      {problem && <p className="text-[11px] text-red-600 mt-0.5">{problem}</p>}
    </div>
  )
}

function TargetPicker({ value, onChange, appRoutes, portalRoutes }: {
  value: string
  onChange: (v: string) => void
  appRoutes: Record<string, RouteInfo>
  portalRoutes: Record<string, RouteInfo>
}) {
  const isUrl = !value.startsWith('app:') && !value.startsWith('portal:')
  return (
    <div className="space-y-1.5">
      <select className={input} value={isUrl ? '__url' : value} onChange={(e) => onChange(e.target.value === '__url' ? 'https://' : e.target.value)} aria-label="Button opens">
        <optgroup label="Employee app">
          {Object.entries(appRoutes).map(([k, r]) => <option key={k} value={`app:${k}`}>App: {r.label}</option>)}
        </optgroup>
        <optgroup label="Admin portal">
          {Object.entries(portalRoutes).map(([k, r]) => <option key={k} value={`portal:${k}`}>Portal: {r.label}</option>)}
        </optgroup>
        <option value="__url">Web link…</option>
      </select>
      {isUrl && (
        <input className={input} value={value} onChange={(e) => onChange(e.target.value.trim())} placeholder="https://" aria-label="Web link" />
      )}
      {isUrl && value && !/^https:\/\/[^\s/]+\.[^\s]+/.test(value) && <p className="text-[11px] text-red-600">Links must start with https://</p>}
    </div>
  )
}

function ImageField({ block, onChange, onUpload }: {
  block: Extract<EditorBlock, { type: 'image' }>
  onChange: (b: EditorBlock) => void
  onUpload: (file: File) => Promise<string>
}) {
  const ref = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  return (
    <div className="space-y-1.5">
      <div className="flex gap-2 items-center">
        <button type="button" disabled={busy} onClick={() => ref.current?.click()} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-md border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-60">
          <ArrowUpTrayIcon className="w-3.5 h-3.5" />{busy ? 'Uploading…' : block.url ? 'Replace image' : 'Upload image'}
        </button>
        <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={async (e) => {
          const f = e.target.files?.[0]
          e.target.value = ''
          if (!f) return
          setBusy(true); setError('')
          try { onChange({ ...block, url: await onUpload(f) }) } catch (err) { setError(err instanceof Error ? err.message : 'Upload failed') } finally { setBusy(false) }
        }} />
        <input className={input} value={block.alt} maxLength={200} onChange={(e) => onChange({ ...block, alt: e.target.value })} placeholder="Alt text (what the image shows)" aria-label="Alt text" />
      </div>
      {block.url && <div className="flex items-center gap-2 text-[11px] text-slate-500"><PhotoIcon className="w-3.5 h-3.5" /><span className="truncate">{block.url}</span></div>}
      {error && <p className="text-[11px] text-red-600">{error}</p>}
    </div>
  )
}

export default function BlockEditor({ blocks, onChange, appRoutes, portalRoutes, onUpload }: {
  blocks: EditorBlock[]
  onChange: (blocks: EditorBlock[]) => void
  appRoutes: Record<string, RouteInfo>
  portalRoutes: Record<string, RouteInfo>
  onUpload: (file: File) => Promise<string>
}) {
  const set = (i: number, b: EditorBlock) => onChange(blocks.map((x, j) => (j === i ? b : x)))
  const move = (i: number, d: -1 | 1) => {
    const j = i + d
    if (j < 0 || j >= blocks.length) return
    const next = [...blocks]
    ;[next[i], next[j]] = [next[j], next[i]]
    onChange(next)
  }

  return (
    <div className="space-y-2">
      {blocks.length === 0 && <p className="text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg px-3 py-4 text-center">No content yet. Add a block below or write with AI.</p>}
      {blocks.map((b, i) => (
        <div key={b.key} className="grid grid-cols-[22px_minmax(0,1fr)_auto] gap-2 items-start rounded-lg border border-slate-200 bg-white px-2.5 py-2">
          <div className="flex flex-col items-center gap-0.5 pt-0.5">
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" className="text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowUpIcon className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={() => move(i, 1)} disabled={i === blocks.length - 1} aria-label="Move down" className="text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowDownIcon className="w-3.5 h-3.5" /></button>
          </div>
          <div className="min-w-0">
            <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400 mb-1">{b.type === 'list' && b.ordered ? 'Numbered list' : KIND_LABEL[b.type]}</div>
            {b.type === 'heading' && (
              <FormatField value={b.text} maxLength={120} bold={false} onChange={(text) => set(i, { ...b, text })} placeholder="Heading" label="Heading" />
            )}
            {b.type === 'paragraph' && (
              <FormatField multiline rows={3} value={b.text} maxLength={2000} onChange={(text) => set(i, { ...b, text })} placeholder="Text" label="Paragraph" />
            )}
            {b.type === 'list' && (() => {
              const ordered = !!b.ordered
              const clean = (items: string[], o: boolean) => items.map((x) => stripListMarker(x, o))
              const typedMarkers = ordered ? b.items.some(hasNumberPrefix) : b.items.some(hasBulletPrefix)
              const suggestNumbered = !ordered && looksNumbered(b.items)
              return (
                <>
                  <FormatField
                    multiline rows={3} value={b.items.join('\n')} label="List items" placeholder="One item per line"
                    onChange={(v) => set(i, { ...b, items: v.split('\n').slice(0, 20) })}
                    onLeave={() => { const items = clean(b.items, ordered); if (items.some((x, j) => x !== b.items[j])) set(i, { ...b, items }) }}
                  />
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <small className="text-[11px] text-slate-400">
                      {suggestNumbered ? (
                        <>These look numbered.{' '}
                          <button type="button" className="font-semibold text-blue-600 hover:underline" onClick={() => set(i, { ...b, ordered: true, items: clean(b.items, true) })}>Switch to numbered</button>
                        </>
                      ) : typedMarkers
                        ? `${ordered ? 'Numbers' : 'Bullets'} are added for you. Typed ones are removed when you leave the field.`
                        : 'One item per line'}
                    </small>
                    <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-500 shrink-0">
                      <input type="checkbox" checked={ordered} onChange={(e) => set(i, { ...b, ordered: e.target.checked, items: e.target.checked ? clean(b.items, true) : b.items })} className="accent-blue-600" />Numbered
                    </label>
                  </div>
                </>
              )
            })()}
            {b.type === 'callout' && (
              <div className="space-y-1.5">
                <select className={`${input} w-auto`} value={b.tone} onChange={(e) => set(i, { ...b, tone: e.target.value as 'info' | 'warn' })} aria-label="Callout tone">
                  <option value="info">Info</option>
                  <option value="warn">Warning</option>
                </select>
                <FormatField multiline rows={2} value={b.text} maxLength={600} onChange={(text) => set(i, { ...b, text: text.replace(/\n/g, ' ') })} placeholder="Something people need to know" label="Callout text" />
              </div>
            )}
            {b.type === 'image' && <ImageField block={b} onChange={(nb) => set(i, nb)} onUpload={onUpload} />}
            {b.type === 'button' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                <input className={input} value={b.label} maxLength={40} onChange={(e) => set(i, { ...b, label: e.target.value })} placeholder="Button label" aria-label="Button label" />
                <TargetPicker value={b.target} onChange={(target) => set(i, { ...b, target })} appRoutes={appRoutes} portalRoutes={portalRoutes} />
              </div>
            )}
          </div>
          <button type="button" onClick={() => onChange(blocks.filter((_, j) => j !== i))} aria-label="Remove block" className="text-slate-400 hover:text-red-600 p-0.5"><XMarkIcon className="w-4 h-4" /></button>
        </div>
      ))}
      <div className="flex flex-wrap gap-1.5 pt-1">
        {(['heading', 'paragraph', 'list', 'callout', 'image', 'button'] as const).map((t) => (
          <button key={t} type="button" onClick={() => onChange([...blocks, blank(t)])} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-slate-700">
            <PlusIcon className="w-3.5 h-3.5" />{t === 'list' ? 'List' : KIND_LABEL[t]}
          </button>
        ))}
      </div>
    </div>
  )
}
